import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import FieldLayout from '../components/FieldLayout';
import FieldEventDialog from '../components/field/FieldEventDialog';
import FieldEventDetails from '../components/field/FieldEventDetails';
import { CalendarsDialog, CategoriesDialog, RecycleBinDialog, TechColorsDialog } from '../components/field/FieldManageDialogs';
import { EventDisplaySettings } from '../components/field/EventDisplaySettings';
import useFieldDisplayPreferences from '../hooks/useFieldDisplayPreferences';
import { formatFieldEvent } from '../utils/fieldDisplay';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, Settings2, CalendarDays, List, Trash2, FolderKanban, Palette, MapPin, User as UserIcon, Users as UsersIcon, Building2, Sun, Moon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEUTRAL = '#94a3b8';
const LANE_H = 15;
const HEADER_H = 26;
const WEEKS_INITIAL = 12;
const WEEKS_STEP = 8;

const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const occursOn = (ev, date) => ev.date <= date && (ev.end_date || ev.date) >= date;
const daysBetween = (from, to) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);
const shiftIsoDate = (value, days) => {
  const d = new Date(`${value}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

// Monday of the week containing a local Y/M/D date. Uses UTC noon to avoid TZ drift.
function mondayOfLocalDate(y, m, d) {
  const dt = new Date(Date.UTC(y, m, d, 12));
  const dow = (dt.getUTCDay() + 6) % 7; // Mon=0..Sun=6
  dt.setUTCDate(dt.getUTCDate() - dow);
  return dt.toISOString().slice(0, 10);
}

// Build a single week starting at a Monday-ISO date.
function weekFromMonday(mondayIso, todayIso) {
  const base = new Date(`${mondayIso}T12:00:00Z`);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(base);
    d.setUTCDate(d.getUTCDate() + i);
    const date = d.toISOString().slice(0, 10);
    days.push({
      date,
      day: d.getUTCDate(),
      month: d.getUTCMonth(),
      year: d.getUTCFullYear(),
      today: date === todayIso,
    });
  }
  return days;
}

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const hh = h % 12 || 12;
  return m ? `${hh}:${String(m).padStart(2, '0')}${ampm}` : `${hh}${ampm}`;
};

const measureCtx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
const measureChip = (text) => {
  if (!measureCtx || !text) return 0;
  measureCtx.font = 'bold 11px ui-sans-serif, system-ui, -apple-system, sans-serif';
  return measureCtx.measureText(text).width;
};

export default function FieldCalendar() {
  const { user } = useAuth();
  const display = useFieldDisplayPreferences(user);
  const perms = user?.effective_permissions || [];
  const canEdit = perms.includes('field.calendar.edit');
  const canAssign = perms.includes('field.assign');
  const canManage = perms.includes('field.schedule.manage');

  // Snapshot the current system date once, at mount, so every render uses the
  // same "today" reference. The calendar's initial view ALWAYS defaults to
  // this snapshot's month/year — never influenced by DB events or state churn.
  const todayRef = useRef(new Date());
  const today = todayRef.current;
  const todayIso = iso(today.getFullYear(), today.getMonth(), today.getDate());
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth());
  const [view, setView] = useState('month');
  const [calendars, setCalendars] = useState([]);
  const [activeCalId, setActiveCalId] = useState('');
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorEvent, setEditorEvent] = useState(null);
  const [editorDefaultDate, setEditorDefaultDate] = useState(null);
  const [detailsEvent, setDetailsEvent] = useState(null);
  const [dayDialogDate, setDayDialogDate] = useState(null);
  const [manageOpen, setManageOpen] = useState('');
  const [dragId, setDragId] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [pendingMove, setPendingMove] = useState(null); // { ev, targetDate }
  const [selectedDate, setSelectedDate] = useState(null);
  const [defaultAllDay, setDefaultAllDay] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [rowLanes, setRowLanes] = useState(() => {
    const v = parseInt(localStorage.getItem('fieldCalRowLanes') || '12', 10);
    return Number.isNaN(v) ? 12 : Math.min(20, Math.max(2, v));
  });
  const setRowLanesPersist = (n) => {
    setRowLanes(n);
    localStorage.setItem('fieldCalRowLanes', String(n));
  };
  const [dark, setDark] = useState(() => localStorage.getItem('fieldCalDark') === '1');

  // Continuous scroll: firstMonday anchors the top of the rendered week list.
  // We pre-load 4 weeks BEFORE the current-month's first Monday so the top
  // sentinel is safely off-screen after we scroll to today's month, avoiding
  // an immediate prepend-race that can bump the label off the current month.
  const [firstMonday, setFirstMonday] = useState(() => {
    const t = new Date();
    return shiftIsoDate(mondayOfLocalDate(t.getFullYear(), t.getMonth(), 1), -4 * 7);
  });
  const [weekCount, setWeekCount] = useState(WEEKS_INITIAL + 4);
  const scrollRootRef = useRef(null);
  const weekRowRefs = useRef({}); // monday -> DOM node
  // Suppress observer-driven label updates until initial scroll settles.
  const settledRef = useRef(false);
  // Once the user manually scrolls/navigates, stop re-anchoring to today.
  const interactedRef = useRef(false);
  const cleanupRef = useRef(null);

  const toggleDark = () => {
    setDark((d) => {
      localStorage.setItem('fieldCalDark', d ? '0' : '1');
      return !d;
    });
  };

  const T = dark ? {
    grid: 'bg-neutral-800 border-neutral-700',
    weekHdr: 'border-neutral-700 bg-neutral-900',
    weekHdrText: 'text-neutral-300',
    cellBorder: 'border-neutral-700',
    cellIn: 'bg-neutral-800',
    cellOut: 'bg-neutral-900/70',
    dragOver: 'ring-2 ring-inset ring-sky-500 bg-sky-950',
    cellHover: 'hover:bg-neutral-700/50',
    dayNum: 'text-neutral-200',
    dayNumOut: 'text-neutral-500',
    more: 'text-neutral-300 hover:text-white',
    card: 'bg-neutral-800 border-neutral-700',
    cardHdr: 'bg-neutral-900 border-neutral-700 text-neutral-100',
    rowHover: 'hover:bg-neutral-700',
    rowDivide: 'divide-neutral-700',
    title: 'text-neutral-100',
    meta: 'text-neutral-300',
    legend: 'text-neutral-200',
    empty: 'bg-neutral-800 border-neutral-700 text-neutral-300',
    dayBtn: 'border-neutral-700 hover:border-neutral-400',
  } : {
    grid: 'bg-white border-slate-200',
    weekHdr: 'border-slate-200 bg-slate-50',
    weekHdrText: 'text-slate-500',
    cellBorder: 'border-slate-100',
    cellIn: 'bg-white',
    cellOut: 'bg-slate-50/70',
    dragOver: 'ring-2 ring-inset ring-sky-400 bg-sky-50',
    cellHover: 'hover:bg-sky-50/80',
    dayNum: 'text-slate-600',
    dayNumOut: 'text-slate-300',
    more: 'text-slate-500 hover:text-slate-900',
    card: 'bg-white border-slate-200',
    cardHdr: 'bg-slate-50 border-slate-200 text-slate-700',
    rowHover: 'hover:bg-slate-50',
    rowDivide: 'divide-slate-100',
    title: 'text-slate-900',
    meta: 'text-slate-500',
    legend: 'text-slate-600',
    empty: 'bg-white border-slate-200 text-slate-500',
    dayBtn: 'border-slate-200 hover:border-slate-400',
  };

  const outlineBtn = dark ? 'bg-neutral-700 border-neutral-600 text-neutral-100 hover:bg-neutral-600 hover:text-white' : '';

  const loadCalendars = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/field/calendars`);
      setCalendars(res.data);
      setActiveCalId((prev) => (prev && res.data.some((c) => c.id === prev) ? prev : res.data[0]?.id || ''));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load calendars');
    }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/field/events`);
      setEvents(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load events');
    }
  }, []);

  const loadUsers = useCallback(() => {
    axios.get(`${API}/field/users`).then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const loadCustomers = useCallback(() => {
    axios.get(`${API}/customers`).then((r) => setCustomers([...r.data].sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
  }, []);

  useEffect(() => {
    loadCalendars();
    loadEvents();
    loadUsers();
    loadCustomers();
    axios.get(`${API}/field/settings`).then((r) => setDefaultAllDay(!!r.data.default_all_day)).catch(() => {});
  }, [loadCalendars, loadEvents, loadUsers, loadCustomers]);

  useEffect(() => {
    if (!activeCalId) return;
    axios.get(`${API}/field/categories/${activeCalId}`).then((r) => setCategories(r.data)).catch(() => {});
  }, [activeCalId]);

  const catById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const userById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const calEvents = useMemo(() => events.filter((e) => e.calendar_id === activeCalId), [events, activeCalId]);

  const colorsFor = (ev) => {
    const cols = (ev.attendees || []).map((a) => userById[a]?.field_color || NEUTRAL);
    return cols.length ? cols : [NEUTRAL];
  };
  const chipStyle = (ev) => {
    const cols = [...new Set(colorsFor(ev))];
    if (cols.length === 1) return { backgroundColor: cols[0] };
    const stops = cols.map((c, i) => `${c} ${(i / cols.length) * 100}% ${((i + 1) / cols.length) * 100}%`).join(', ');
    return { background: `linear-gradient(to bottom, ${stops})` };
  };

  const chipTextColor = () => '#000000'; // event lettering is always black (user preference)
  const chipLabel = useCallback((ev) => formatFieldEvent(ev, display.preferences), [display.preferences]);

  // Day-column width drives whether a single-day chip earns a second text line.
  const [colWidth, setColWidth] = useState(0);
  useEffect(() => {
    const el = scrollRootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let frame;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const width = el.clientWidth / 7;
        setColWidth((previous) => Math.abs(previous - width) > 0.5 ? width : previous);
      });
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => { ro.disconnect(); cancelAnimationFrame(frame); };
  }, [view]);

  const legendUsers = useMemo(() => {
    const ids = new Set();
    calEvents.forEach((ev) => (ev.attendees || []).forEach((a) => ids.add(a)));
    return users.filter((u) => ids.has(u.id));
  }, [calEvents, users]);

  const weeks = useMemo(() => {
    const out = [];
    for (let w = 0; w < weekCount; w++) {
      const monday = shiftIsoDate(firstMonday, w * 7);
      const days = weekFromMonday(monday, todayIso);
      const weekStart = days[0].date;
      const weekEnd = days[6].date;
      const evs = calEvents
        .filter((ev) => ev.date <= weekEnd && (ev.end_date || ev.date) >= weekStart)
        .sort((a, b) =>
          a.date.localeCompare(b.date) ||
          daysBetween(b.date, b.end_date || b.date) - daysBetween(a.date, a.end_date || a.date) ||
          (b.all_day - a.all_day) ||
          (a.start_time || '').localeCompare(b.start_time || '') ||
          a.title.localeCompare(b.title));
      const laneEnds = [];
      const placed = [];
      for (const ev of evs) {
        const startIdx = Math.max(0, daysBetween(weekStart, ev.date));
        const endIdx = Math.min(6, daysBetween(weekStart, ev.end_date || ev.date));
        let lane = laneEnds.findIndex((end) => end < startIdx);
        if (lane === -1) { lane = laneEnds.length; laneEnds.push(endIdx); } else { laneEnds[lane] = endIdx; }
        placed.push({ ev, startIdx, endIdx, lane });
      }
      const hiddenByDay = Array(7).fill(0);
      for (const s of placed) {
        if (s.lane >= rowLanes) for (let i = s.startIdx; i <= s.endIdx; i++) hiddenByDay[i]++;
      }
      // Single-day chips get a second text line whenever the slot directly
      // below them is free in that day's column — so they shrink back to one
      // line automatically as soon as the day fills up.
      const occupied = Array.from({ length: rowLanes }, () => Array(7).fill(false));
      const visible = placed.filter((s) => s.lane < rowLanes);
      for (const s of visible) {
        for (let i = s.startIdx; i <= s.endIdx; i++) occupied[s.lane][i] = true;
      }
      for (const s of [...visible].sort((a, b) => a.lane - b.lane)) {
        const below = s.lane + 1;
        if (s.startIdx !== s.endIdx || below >= rowLanes) continue;
        if (occupied[below][s.startIdx]) continue;
        const label = `${s.ev.all_day ? '' : `${fmtTime(s.ev.start_time)} `}${chipLabel(s.ev)}`;
        if (!colWidth || measureChip(label) <= colWidth - 14) continue; // fits on one line already
        s.tall = true;
        occupied[below][s.startIdx] = true;
      }
      out.push({
        monday,
        days,
        segments: visible,
        visibleLanes: Math.min(laneEnds.length, rowLanes),
        hiddenByDay,
      });
    }
    return out;
  }, [firstMonday, weekCount, calEvents, todayIso, rowLanes, colWidth, chipLabel]);

  const scrollToWeek = useCallback((monday, smooth = true) => {
    const el = weekRowRefs.current[monday];
    const root = scrollRootRef.current;
    if (!el || !root) return;
    const headerEl = root.querySelector('[data-cal-sticky-header]');
    const headerH = headerEl ? headerEl.offsetHeight : 0;
    const rootRect = root.getBoundingClientRect();
    const rowRect = el.getBoundingClientRect();
    const target = rowRect.top - rootRect.top + root.scrollTop - headerH;
    root.scrollTo({ top: Math.max(0, target), behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  const ensureWeekAndScroll = useCallback((targetMonday) => {
    const rangeEnd = shiftIsoDate(firstMonday, (weekCount - 1) * 7);
    if (targetMonday < firstMonday) {
      // Prepend enough weeks; preserve visual position via scroll compensation.
      const missing = Math.ceil(daysBetween(targetMonday, firstMonday) / 7);
      const add = missing + WEEKS_STEP;
      const root = scrollRootRef.current;
      const prevHeight = root ? root.scrollHeight : 0;
      const prevScroll = root ? root.scrollTop : 0;
      setFirstMonday(shiftIsoDate(firstMonday, -add * 7));
      setWeekCount((n) => n + add);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (root) root.scrollTop = prevScroll + (root.scrollHeight - prevHeight);
        setTimeout(() => scrollToWeek(targetMonday), 20);
      }));
    } else if (targetMonday > rangeEnd) {
      const missing = Math.ceil(daysBetween(rangeEnd, targetMonday) / 7);
      setWeekCount((n) => n + missing + WEEKS_STEP);
      setTimeout(() => scrollToWeek(targetMonday), 40);
    } else {
      scrollToWeek(targetMonday);
    }
    // Final snap after smooth scrolling settles so the target row isn't cut off.
    setTimeout(() => scrollToWeek(targetMonday, false), 600);
  }, [firstMonday, weekCount, scrollToWeek]);

  const jumpMonth = (offset) => {
    interactedRef.current = true;
    const d = new Date(year, month + offset, 1);
    const ny = d.getFullYear();
    const nm = d.getMonth();
    setYear(ny);
    setMonth(nm);
    ensureWeekAndScroll(mondayOfLocalDate(ny, nm, 1));
  };
  const goToday = () => {
    interactedRef.current = true;
    setYear(today.getFullYear());
    setMonth(today.getMonth());
    setView('month');
    ensureWeekAndScroll(mondayOfLocalDate(today.getFullYear(), today.getMonth(), today.getDate()));
  };

  const openNew = (date) => {
    setEditorEvent(null);
    // Prefill with the highlighted day if one is selected, otherwise today.
    setEditorDefaultDate(date || selectedDate || todayIso);
    setEditorOpen(true);
  };
  const openEdit = (ev) => {
    setDetailsEvent(null);
    setEditorEvent(ev);
    setEditorOpen(true);
  };
  const openDuplicate = (ev) => {
    setDetailsEvent(null);
    setEditorEvent({ ...ev, id: null, title: `${ev.title} (copy)` });
    setEditorOpen(true);
  };

  const deleteEvent = async (ev) => {
    try {
      await axios.delete(`${API}/field/events/${ev.id}`);
      setDetailsEvent(null);
      loadEvents();
      toast.success('Event moved to recycle bin', {
        action: canManage ? { label: 'Undo', onClick: async () => { await axios.post(`${API}/field/events/${ev.id}/restore`); loadEvents(); } } : undefined,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete event');
    } finally {
      // Radix can leave the body locked when nested modals close together.
      document.body.style.pointerEvents = '';
    }
  };

  const moveEvent = async (ev, targetDate) => {
    const delta = daysBetween(ev.date, targetDate);
    if (!delta) return;
    const prev = { date: ev.date, end_date: ev.end_date };
    const payload = { ...ev, date: shiftIsoDate(ev.date, delta), end_date: shiftIsoDate(ev.end_date || ev.date, delta) };
    try {
      await axios.put(`${API}/field/events/${ev.id}`, payload);
      loadEvents();
      toast.success(`Moved to ${payload.date}`, {
        action: {
          label: 'Undo',
          onClick: async () => {
            await axios.put(`${API}/field/events/${ev.id}`, { ...payload, ...prev });
            loadEvents();
          },
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move event');
    }
  };

  const eventsForDate = (date) =>
    calEvents
      .filter((ev) => occursOn(ev, date))
      .sort((a, b) => (b.all_day - a.all_day) || (a.start_time || '').localeCompare(b.start_time || '') || a.title.localeCompare(b.title));

  const agendaGroups = useMemo(() => {
    const startIso = new Date().toISOString().slice(0, 10);
    const upcoming = calEvents.filter((ev) => (ev.end_date || ev.date) >= startIso);
    const map = new Map();
    for (const ev of upcoming) {
      const key = ev.date >= startIso ? ev.date : startIso;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [calEvents]);

  const monthLabel = new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Track visible weeks and reflect the majority month in the header label.
  useEffect(() => {
    if (view !== 'month') return;
    const root = scrollRootRef.current;
    if (!root) return;
    const update = () => {
      // Ignore observer-driven label updates until the initial scroll settles;
      // this prevents the mount-time prepend from bumping the label off the
      // current system month.
      if (!settledRef.current) return;
      const rootRect = root.getBoundingClientRect();
      const headerEl = root.querySelector('[data-cal-sticky-header]');
      const headerH = headerEl ? headerEl.offsetHeight : 0;
      const viewTop = rootRect.top + headerH;
      const viewBottom = rootRect.bottom;
      // Label follows the top visible week: use the month of its Sunday (last
      // day), so the week containing the 1st already reads as the new month.
      let topWeek = null;
      let topWeekTop = Infinity;
      for (const w of weeks) {
        const node = weekRowRefs.current[w.monday];
        if (!node) continue;
        const r = node.getBoundingClientRect();
        if (r.bottom <= viewTop + 8 || r.top >= viewBottom) continue;
        // Must be at least half visible to count as the top week
        const visible = Math.min(r.bottom, viewBottom) - Math.max(r.top, viewTop);
        if (visible < (r.bottom - r.top) * 0.5) continue;
        if (r.top < topWeekTop) { topWeekTop = r.top; topWeek = w; }
      }
      if (!topWeek) return;
      const sunday = topWeek.days[6];
      setYear((prev) => (prev !== sunday.year ? sunday.year : prev));
      setMonth((prev) => (prev !== sunday.month ? sunday.month : prev));
    };
    root.addEventListener('scroll', update, { passive: true });
    update();
    return () => root.removeEventListener('scroll', update);
  }, [weeks, view]);

  // Extend the window when top / bottom sentinels enter the viewport.
  const topSentinelRef = useRef(null);
  const bottomSentinelRef = useRef(null);
  useEffect(() => {
    if (view !== 'month') return;
    const root = scrollRootRef.current;
    if (!root) return;
    const bottomObs = new IntersectionObserver((entries) => {
      if (!settledRef.current) return;
      if (entries.some((e) => e.isIntersecting)) setWeekCount((n) => n + WEEKS_STEP);
    }, { root, rootMargin: '400px 0px' });
    if (bottomSentinelRef.current) bottomObs.observe(bottomSentinelRef.current);
    const topObs = new IntersectionObserver((entries) => {
      if (!settledRef.current) return;
      if (entries.some((e) => e.isIntersecting)) {
        const prevHeight = root.scrollHeight;
        const prevScroll = root.scrollTop;
        setFirstMonday((prev) => shiftIsoDate(prev, -WEEKS_STEP * 7));
        setWeekCount((n) => n + WEEKS_STEP);
        requestAnimationFrame(() => requestAnimationFrame(() => {
          root.scrollTop = prevScroll + (root.scrollHeight - prevHeight);
        }));
      }
    }, { root, rootMargin: '400px 0px' });
    if (topSentinelRef.current) topObs.observe(topSentinelRef.current);
    return () => { bottomObs.disconnect(); topObs.disconnect(); };
  }, [view, weekCount, firstMonday]);

  // On mount, whenever we switch back into month view, and after events load
  // (row heights change and shift the pixel-based scroll), re-anchor the view
  // to the current system month — until the user manually scrolls/navigates.
  useEffect(() => {
    if (view !== 'month' || interactedRef.current) return;
    settledRef.current = false;
    const now = new Date();
    setYear(now.getFullYear());
    setMonth(now.getMonth());
    const monday = mondayOfLocalDate(now.getFullYear(), now.getMonth(), 1);
    const t = setTimeout(() => {
      scrollToWeek(monday, false);
      // One more pass after layout settles, then hand off to the observer.
      const t2 = setTimeout(() => {
        if (!interactedRef.current) scrollToWeek(monday, false);
        settledRef.current = true;
      }, 150);
      cleanupRef.current = () => clearTimeout(t2);
    }, 30);
    return () => { clearTimeout(t); if (cleanupRef.current) cleanupRef.current(); };
  }, [view, scrollToWeek, calEvents]);

  // Mark manual interaction so we stop re-anchoring to today.
  useEffect(() => {
    if (view !== 'month') return;
    const root = scrollRootRef.current;
    if (!root) return;
    const mark = () => { interactedRef.current = true; };
    root.addEventListener('wheel', mark, { passive: true });
    root.addEventListener('touchstart', mark, { passive: true });
    return () => {
      root.removeEventListener('wheel', mark);
      root.removeEventListener('touchstart', mark);
    };
  }, [view]);

  // Pointer-based drag & drop (works with both mouse and touch, unlike native HTML5 DnD)
  const pointerDragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const ghostRef = useRef(null);
  const [ghost, setGhost] = useState(null);

  const chipPointerDown = (ev) => (e) => {
    if (!canEdit) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    pointerDragRef.current = { ev, startX: e.clientX, startY: e.clientY, active: false, width: rect.width };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };

  const chipPointerMove = (e) => {
    const d = pointerDragRef.current;
    if (!d) return;
    if (!d.active) {
      if (Math.abs(e.clientX - d.startX) + Math.abs(e.clientY - d.startY) < 8) return;
      d.active = true;
      interactedRef.current = true;
      setDragId(d.ev.id);
      setGhost({ ev: d.ev, width: Math.min(d.width, 260), x: e.clientX, y: e.clientY });
    }
    e.preventDefault();
    if (ghostRef.current) {
      ghostRef.current.style.left = `${e.clientX + 10}px`;
      ghostRef.current.style.top = `${e.clientY + 12}px`;
    }
    const cell = document.elementFromPoint(e.clientX, e.clientY)?.closest?.('[data-date]');
    setDragOverDate(cell ? cell.getAttribute('data-date') : null);
    const root = scrollRootRef.current;
    if (root) {
      const r = root.getBoundingClientRect();
      if (e.clientY < r.top + 40) root.scrollTop -= 12;
      else if (e.clientY > r.bottom - 40) root.scrollTop += 12;
    }
  };

  const chipPointerUp = (e) => {
    const d = pointerDragRef.current;
    pointerDragRef.current = null;
    setGhost(null);
    if (!d || !d.active) return;
    const cell = document.elementFromPoint(e.clientX, e.clientY)?.closest?.('[data-date]');
    const target = cell?.getAttribute('data-date');
    setDragId(null);
    setDragOverDate(null);
    if (target && daysBetween(d.ev.date, target)) setPendingMove({ ev: d.ev, targetDate: target });
    suppressClickRef.current = true;
    setTimeout(() => { suppressClickRef.current = false; }, 50);
  };

  const chipPointerCancel = () => {
    pointerDragRef.current = null;
    setGhost(null);
    setDragId(null);
    setDragOverDate(null);
  };

  const renderSegment = (seg, days) => {
    const { ev, startIdx, endIdx, lane, tall } = seg;
    const starts = ev.date >= days[0].date;
    const ends = (ev.end_date || ev.date) <= days[6].date;
    const span = endIdx - startIdx + 1;
    const plainLabel = chipLabel(ev);
    return (
      <div
        key={`${ev.id}-${days[0].date}`}
        onPointerDown={chipPointerDown(ev)}
        onPointerMove={chipPointerMove}
        onPointerUp={chipPointerUp}
        onPointerCancel={chipPointerCancel}
        onClick={(e) => { e.stopPropagation(); if (suppressClickRef.current) return; setDetailsEvent(ev); }}
        data-testid={`field-event-chip-${ev.id}`}
        className={`absolute px-1 text-[11px] leading-[13px] cursor-pointer transition-opacity hover:opacity-80 select-none ${
          tall ? 'whitespace-normal overflow-hidden' : 'truncate'} ${
          span > 1 ? 'text-center' : ''} ${starts ? 'rounded-l' : ''} ${ends ? 'rounded-r' : ''} ${
          dragId === ev.id ? 'opacity-40' : ''} ${dragId ? 'pointer-events-none' : 'pointer-events-auto'}`}
        style={{
          ...chipStyle(ev),
          color: chipTextColor(ev),
          top: lane * LANE_H,
          height: (tall ? LANE_H * 2 : LANE_H) - 2,
          left: `calc(${(startIdx / 7) * 100}% + 2px)`,
          width: `calc(${(span / 7) * 100}% - 4px)`,
          touchAction: canEdit ? 'none' : 'auto',
          ...(tall ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' } : {}),
        }}
        title={plainLabel}
      >
        <div className={`min-w-0 max-w-full font-bold ${tall ? '[overflow-wrap:anywhere]' : 'truncate'}`} data-testid={`field-event-label-${ev.id}-${days[0].date}`}>
          {!ev.all_day && <span className="opacity-80 mr-1 font-medium">{fmtTime(ev.start_time)}</span>}
          {plainLabel}
        </div>
      </div>
    );
  };

  return (
    <FieldLayout title="Field Calendar" dark={dark}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" onClick={() => jumpMonth(-1)} data-testid="field-cal-prev" className={outlineBtn}><ChevronLeft size={16} /></Button>
          <Button variant="outline" size="icon" onClick={() => jumpMonth(1)} data-testid="field-cal-next" className={outlineBtn}><ChevronRight size={16} /></Button>
        </div>
        <h3 className={`text-lg font-bold min-w-[170px] ${dark ? 'text-neutral-100' : 'text-slate-900'}`} data-testid="field-cal-month-label">{monthLabel}</h3>
        <Button variant="outline" size="sm" onClick={goToday} data-testid="field-cal-today" className={outlineBtn}>Today</Button>
        <div className={`flex rounded-md border overflow-hidden ${dark ? 'border-neutral-600' : 'border-slate-200'}`}>
          <button
            onClick={() => setView('month')}
            data-testid="field-view-month"
            className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 ${view === 'month' ? 'bg-sky-600 text-white' : dark ? 'bg-neutral-700 text-neutral-100 hover:bg-neutral-600' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
          ><CalendarDays size={14} /> Month</button>
          <button
            onClick={() => setView('agenda')}
            data-testid="field-view-agenda"
            className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 ${view === 'agenda' ? 'bg-sky-600 text-white' : dark ? 'bg-neutral-700 text-neutral-100 hover:bg-neutral-600' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
          ><List size={14} /> Agenda</button>
        </div>
        <Button variant="outline" size="icon" onClick={toggleDark} data-testid="field-cal-theme-toggle" title={dark ? 'Switch to light view' : 'Switch to dark view'} className={outlineBtn}>
          {dark ? <Sun size={16} /> : <Moon size={16} />}
        </Button>
        <Button variant="outline" size="icon" onClick={() => setSettingsOpen(true)} data-testid="field-cal-settings-btn" title="Calendar settings" className={outlineBtn}>
          <Settings2 size={16} />
        </Button>
        <div className="flex-1" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="field-manage-menu" className={outlineBtn}><Settings2 size={14} className="mr-1.5" /> Manage</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="field-manage-options" className={dark ? 'bg-neutral-800 border-neutral-700 text-neutral-100' : ''}>
              {canManage && <>
              <DropdownMenuItem onClick={() => setManageOpen('calendars')} data-testid="manage-calendars-item" className={dark ? 'focus:bg-neutral-700 focus:text-white' : ''}><FolderKanban size={14} className="mr-2" /> Calendars</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setManageOpen('categories')} data-testid="manage-categories-item" className={dark ? 'focus:bg-neutral-700 focus:text-white' : ''}><Palette size={14} className="mr-2" /> Categories</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setManageOpen('techcolors')} data-testid="manage-tech-colors-item" className={dark ? 'focus:bg-neutral-700 focus:text-white' : ''}><UsersIcon size={14} className="mr-2" /> Technician Colors</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setManageOpen('recycle')} data-testid="manage-recycle-item" className={dark ? 'focus:bg-neutral-700 focus:text-white' : ''}><Trash2 size={14} className="mr-2" /> Recycle Bin</DropdownMenuItem>
              </>}
              <DropdownMenuItem onClick={() => setSettingsOpen(true)} data-testid="manage-settings-item" className={dark ? 'focus:bg-neutral-700 focus:text-white' : ''}><Settings2 size={14} className="mr-2" /> Calendar Settings</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        {canEdit && (
          <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => openNew()} data-testid="field-new-event-btn">
            <Plus size={14} className="mr-1.5" /> New Event
          </Button>
        )}
      </div>

      {/* Calendar tabs */}
      {calendars.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="field-calendar-tabs">
          {calendars.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveCalId(c.id)}
              data-testid={`field-cal-tab-${c.id}`}
              className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                activeCalId === c.id
                  ? dark
                    ? 'bg-sky-600 text-white border-sky-600'
                    : 'bg-slate-900 text-white border-slate-900'
                  : dark
                    ? 'bg-neutral-800 text-neutral-100 border-neutral-600 hover:border-neutral-400'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      {/* Technician legend */}
      {legendUsers.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="field-tech-legend">
          {legendUsers.map((u) => (
            <span key={u.id} className={`flex items-center gap-1.5 text-xs ${T.legend}`}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: u.field_color || NEUTRAL }} />
              {u.name}
            </span>
          ))}
        </div>
      )}

      {view === 'month' ? (
        <div
          ref={scrollRootRef}
          className={`border rounded-lg overflow-y-auto shadow-sm ${T.grid}`}
          style={{ maxHeight: 'calc(100vh - 14rem)' }}
          data-testid="field-month-grid"
        >
          <div data-cal-sticky-header className={`grid grid-cols-7 border-b sticky top-0 z-10 ${T.weekHdr}`}>
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <div key={d} className={`px-2 py-2 text-xs font-semibold uppercase tracking-wider text-center ${T.weekHdrText}`}>{d}</div>
            ))}
          </div>
          <div ref={topSentinelRef} style={{ height: 1 }} />
          {weeks.map(({ monday, days, segments, hiddenByDay }) => {
            const rowH = HEADER_H + rowLanes * LANE_H + 26;
            return (
              <div
                key={monday}
                data-monday={monday}
                ref={(el) => { if (el) weekRowRefs.current[monday] = el; else delete weekRowRefs.current[monday]; }}
                className="relative"
              >
                <div className="grid grid-cols-7">
                  {days.map((cell, i) => {
                    const outside = cell.year !== year || cell.month !== month;
                    return (
                      <div
                        key={cell.date}
                        onClick={() => {
                          setSelectedDate(cell.date);
                          if (!canEdit && eventsForDate(cell.date).length) setDayDialogDate(cell.date);
                        }}
                        onDoubleClick={() => { if (canEdit) openNew(cell.date); }}
                        data-date={cell.date}
                        data-testid={`field-day-cell-${cell.date}`}
                        className={`flex flex-col border-b border-r ${T.cellBorder} p-1 transition-colors ${canEdit ? `cursor-pointer ${T.cellHover}` : T.cellHover} ${
                          outside ? T.cellOut : T.cellIn} ${dragOverDate === cell.date ? T.dragOver : ''} ${selectedDate === cell.date ? 'ring-2 ring-inset ring-sky-500' : ''}`}
                        style={{ height: rowH }}
                      >
                        <div className="flex items-center justify-between px-1">
                          <span className={`text-xs font-semibold ${cell.today ? 'bg-sky-600 text-white rounded-full w-5 h-5 flex items-center justify-center' : outside ? T.dayNumOut : T.dayNum}`}>
                            {cell.day === 1 ? (
                              <span className="whitespace-nowrap">{new Date(`${cell.date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short' })} 1</span>
                            ) : cell.day}
                          </span>
                        </div>
                        {hiddenByDay[i] > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDayDialogDate(cell.date); }}
                            data-testid={`field-more-${cell.date}`}
                            className={`mt-auto text-[11px] font-medium px-1 text-left ${T.more}`}
                          >
                            +{hiddenByDay[i]} more
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="absolute left-0 right-0 pointer-events-none" style={{ top: HEADER_H }}>
                  {segments.map((seg) => renderSegment(seg, days))}
                </div>
              </div>
            );
          })}
          <div ref={bottomSentinelRef} style={{ height: 1 }} />
        </div>
      ) : (
        <div className="space-y-4" data-testid="field-agenda-view">
          {agendaGroups.length === 0 && (
            <div className={`border rounded-lg p-10 text-center text-sm ${T.empty}`}>No upcoming events on this calendar.</div>
          )}
          {agendaGroups.map(([date, list]) => (
            <div key={date} className={`border rounded-lg shadow-sm overflow-hidden ${T.card}`}>
              <div className={`px-4 py-2 border-b text-sm font-bold ${T.cardHdr}`}>
                {new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
              <div className={`divide-y ${T.rowDivide}`}>
                {list.map((ev) => {
                  return (
                    <button
                      key={ev.id}
                      onClick={() => setDetailsEvent(ev)}
                      data-testid={`field-agenda-event-${ev.id}`}
                      className={`w-full text-left px-4 py-3 flex items-start gap-3 ${T.rowHover}`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0" style={chipStyle(ev)} />
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-semibold truncate ${T.title}`} title={chipLabel(ev)} data-testid={`field-agenda-label-${ev.id}-${date}`}>{chipLabel(ev)}</p>
                        <div className={`flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5 text-xs ${T.meta}`}>
                          <span>{ev.all_day ? 'All day' : `${fmtTime(ev.start_time)} – ${fmtTime(ev.end_time)}`}</span>
                          {ev.customer_name && <span className="flex items-center gap-1"><Building2 size={11} /> {ev.customer_name}</span>}
                          {(ev.location || ev.lsd) && <span className="flex items-center gap-1"><MapPin size={11} /> {[ev.location, ev.lsd].filter(Boolean).join(' · ')}</span>}
                          {(ev.attendees || []).length > 0 && (
                            <span className="flex items-center gap-1">
                              <UserIcon size={11} /> {ev.attendees.map((a) => userById[a]?.name || '?').join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Day drill-down */}
      <Dialog open={!!dayDialogDate} onOpenChange={(o) => !o && setDayDialogDate(null)}>
        <DialogContent className={`grid-cols-[minmax(0,1fr)] max-w-md ${dark ? 'dark bg-neutral-800 border-neutral-700 text-neutral-100' : ''}`} data-testid="field-day-dialog">
          <DialogHeader>
            <DialogTitle>
              {dayDialogDate && new Date(`${dayDialogDate}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </DialogTitle>
            <DialogDescription className="sr-only">Field events for the selected day.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 max-h-96 overflow-y-auto" data-testid="field-day-dialog-list">
            {dayDialogDate && eventsForDate(dayDialogDate).map((ev) => (
              <button
                key={ev.id}
                onClick={() => { setDayDialogDate(null); setDetailsEvent(ev); }}
                data-testid={`field-day-event-${ev.id}`}
                className={`w-full text-left px-3 py-2 rounded-md border flex items-center gap-2 ${T.dayBtn}`}
              >
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={chipStyle(ev)} />
                <span className={`text-sm font-medium truncate min-w-0 flex-1 ${T.title}`} title={chipLabel(ev)} data-testid={`field-day-event-label-${ev.id}`}>{chipLabel(ev)}</span>
                <span className={`text-xs ${T.meta}`}>{ev.all_day ? 'All day' : fmtTime(ev.start_time)}</span>
              </button>
            ))}
          </div>
          {canEdit && dayDialogDate && (
            <Button size="sm" className="bg-sky-600 hover:bg-sky-700" data-testid="field-day-add-event" onClick={() => { const d = dayDialogDate; setDayDialogDate(null); openNew(d); }}>
              <Plus size={14} className="mr-1.5" /> Add event this day
            </Button>
          )}
        </DialogContent>
      </Dialog>

      {/* Floating ghost chip that follows the cursor while dragging */}
      {ghost && (
        <div
          ref={ghostRef}
          className="fixed z-[100] pointer-events-none px-1 text-[11px] leading-[13px] truncate rounded shadow-xl select-none"
          style={{
            left: ghost.x + 10,
            top: ghost.y + 12,
            width: ghost.width,
            height: LANE_H - 2,
            ...chipStyle(ghost.ev),
            color: chipTextColor(ghost.ev),
          }}
          data-testid="field-drag-ghost"
        >
          {!ghost.ev.all_day && <span className="opacity-80 mr-1 font-medium">{fmtTime(ghost.ev.start_time)}</span>}
          <span className="font-bold">{chipLabel(ghost.ev)}</span>
        </div>
      )}

      {/* Move confirm */}
      <AlertDialog open={!!pendingMove} onOpenChange={(o) => !o && setPendingMove(null)}>
        <AlertDialogContent className={dark ? 'dark bg-neutral-800 border-neutral-700 text-neutral-100' : ''} data-testid="field-move-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Move this event?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingMove && `"${chipLabel(pendingMove.ev)}" will move from ${pendingMove.ev.date} to ${pendingMove.targetDate}${(pendingMove.ev.end_date && pendingMove.ev.end_date !== pendingMove.ev.date) ? ' (multi-day span shifts with it)' : ''}.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="field-move-cancel">No</AlertDialogCancel>
            <AlertDialogAction className="bg-sky-600 hover:bg-sky-700" onClick={() => { const m = pendingMove; setPendingMove(null); if (m) moveEvent(m.ev, m.targetDate); }} data-testid="field-move-confirm">
              Yes, move it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Calendar settings */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className={`grid-cols-[minmax(0,1fr)] sm:max-w-lg max-h-[90dvh] overflow-y-auto ${dark ? 'dark bg-neutral-800 border-neutral-700 text-neutral-100' : ''}`} data-testid="field-settings-dialog">
          <DialogHeader>
            <DialogTitle>Calendar Settings</DialogTitle>
            <DialogDescription className="sr-only">Personal calendar display preferences and available calendar defaults.</DialogDescription>
          </DialogHeader>
          <EventDisplaySettings open={settingsOpen} preferences={display.preferences} loading={display.loading} error={display.error} onSave={display.save} onRetry={display.retry} dark={dark} />
          <div className="py-1 space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="font-semibold">Row size</Label>
              <span className={`text-xs font-medium ${dark ? 'text-neutral-400' : 'text-slate-500'}`}>{rowLanes} events per day</span>
            </div>
            <p className={`text-xs ${dark ? 'text-neutral-400' : 'text-slate-500'}`}>All weeks use the same height. Extra events show as "+n more". Saved on this device.</p>
            <input
              type="range"
              min="2"
              max="20"
              step="1"
              value={rowLanes}
              onChange={(e) => setRowLanesPersist(parseInt(e.target.value, 10))}
              className="w-full accent-sky-600"
              data-testid="field-row-size-slider"
            />
          </div>
          {canManage && (
          <div className="flex items-center justify-between gap-3 py-1">
            <div>
              <Label className="font-semibold">Default new events to "All day"</Label>
              <p className={`text-xs mt-0.5 ${dark ? 'text-neutral-400' : 'text-slate-500'}`}>Applies to everyone in your organization.</p>
            </div>
            <Switch
              checked={defaultAllDay}
              onCheckedChange={async (v) => {
                setDefaultAllDay(v);
                try {
                  await axios.put(`${API}/field/settings`, { default_all_day: v });
                  toast.success(v ? 'New events will default to all day' : 'New events will default to timed');
                } catch (err) {
                  setDefaultAllDay(!v);
                  toast.error(err.response?.data?.detail || 'Failed to save setting');
                }
              }}
              data-testid="field-default-allday-switch"
            />
          </div>
          )}
        </DialogContent>
      </Dialog>

      <FieldEventDialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={() => { setEditorOpen(false); loadEvents(); }}
        event={editorEvent}
        defaultDate={editorDefaultDate}
        calendars={calendars}
        activeCalId={activeCalId}
        users={users}
        customers={customers}
        canAssign={canAssign}
        onCustomersChanged={loadCustomers}
        defaultAllDay={defaultAllDay}
        onDelete={(ev) => { setEditorOpen(false); deleteEvent(ev); }}
        dark={dark}
      />

      <FieldEventDetails
        event={detailsEvent}
        displayName={detailsEvent ? chipLabel(detailsEvent) : ''}
        onClose={() => setDetailsEvent(null)}
        catById={catById}
        userById={userById}
        canEdit={canEdit}
        canOpenJob={perms.includes('job-detail')}
        onEdit={openEdit}
        onDuplicate={openDuplicate}
        onDelete={deleteEvent}
        dark={dark}
      />

      <CalendarsDialog open={manageOpen === 'calendars'} onClose={() => setManageOpen('')} calendars={calendars} onChanged={loadCalendars} />
      <CategoriesDialog
        open={manageOpen === 'categories'}
        onClose={() => setManageOpen('')}
        calendarId={activeCalId}
        categories={categories}
        onChanged={() => activeCalId && axios.get(`${API}/field/categories/${activeCalId}`).then((r) => setCategories(r.data))}
      />
      <TechColorsDialog open={manageOpen === 'techcolors'} onClose={() => setManageOpen('')} users={users} onChanged={loadUsers} />
      <RecycleBinDialog open={manageOpen === 'recycle'} onClose={() => setManageOpen('')} onRestored={loadEvents} catById={catById} />
    </FieldLayout>
  );
}
